// VRV Design System — Premium UI tokens & reusable components
import React from 'react';

export const T = {
  bg: '#F8F5EE',
  white: '#FFFFFF',
  navy: '#16213E',
  navyDark: '#0F172A',
  gold: '#D4AF37',
  goldDark: '#B9932F',
  ink: '#111827',
  muted: '#6B7280',
  border: 'rgba(0,0,0,0.07)',
  success: '#22C55E',
  danger: '#EF4444',
  // Legacy compat
  dark: '#16213E',
  stripe: '#F1EDE4',
};

// ── Status badge helper ──────────────────────────────────────────────────────
export const statusBadge = (status) => {
  if (!status) return {};
  const s = status.toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE') return { color: '#15803d', backgroundColor: '#dcfce7', border: '1px solid #bbf7d0' };
  if (s === 'PENDING') return { color: '#a16207', backgroundColor: '#fefce8', border: '1px solid #fde68a' };
  return { color: '#b91c1c', backgroundColor: '#fef2f2', border: '1px solid #fecaca' };
};

export const getStatusClass = (status) => {
  if (!status) return 'badge badge-muted';
  const s = status.toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE') return 'badge badge-success';
  if (s === 'PENDING') return 'badge badge-warning';
  if (s === 'INACTIVE' || s === 'SUSPENDED') return 'badge badge-danger';
  return 'badge badge-muted';
};

// ── Input helpers ────────────────────────────────────────────────────────────
export const inputCls = 'vrv-input';
export const inputStyle = { background: 'white', border: '1.5px solid rgba(0,0,0,0.1)', color: '#111827' };
export const onFocus = () => {};
export const onBlur = () => {};

// ── Visibility badge helper (for content visibility) ─────────────────────────
export const visibilityBadge = (vis) => vis === 'PUBLIC'
  ? { color: '#15803d', backgroundColor: '#dcfce7', border: '1px solid #bbf7d0' }
  : { color: '#6B7280', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' };

// ── Table primitives ─────────────────────────────────────────────────────────
export const Th = ({ children, center }) => (
  <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap ${center ? 'text-center' : 'text-left'}`}
    style={{ color: T.muted, background: '#F1EDE4', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
    {children}
  </th>
);

export const Tr = ({ children, selected }) => (
  <tr className="group transition-colors duration-150"
    style={{ borderBottom: '1px solid rgba(0,0,0,0.05)', backgroundColor: selected ? 'rgba(212,175,55,0.06)' : 'white' }}
    onMouseEnter={e => { if (!selected) e.currentTarget.style.backgroundColor = '#FAFAF8'; }}
    onMouseLeave={e => { if (!selected) e.currentTarget.style.backgroundColor = 'white'; }}>
    {children}
  </tr>
);

// ── Action Button ─────────────────────────────────────────────────────────────
const ACT_COLORS = {
  green:  { bg: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' },
  red:    { bg: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' },
  gold:   { bg: 'rgba(212,175,55,0.1)', color: '#B9932F', border: '1px solid rgba(212,175,55,0.3)' },
  muted:  { bg: '#f3f4f6', color: '#6B7280', border: '1px solid #e5e7eb' },
  dark:   { bg: T.navy, color: 'white', border: `1px solid ${T.navy}` },
};

export const ActionBtn = ({ onClick, disabled, color = 'muted', children, small }) => {
  const s = ACT_COLORS[color] || ACT_COLORS.muted;
  return (
    <button onClick={onClick} disabled={disabled}
      className={`inline-flex items-center justify-center font-bold uppercase tracking-wider rounded-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 ${small ? 'px-2.5 py-1 text-[10px]' : 'px-3.5 py-1.5 text-[11px]'}`}
      style={{ background: s.bg, color: s.color, border: s.border, minHeight: '32px', fontFamily: 'Poppins, sans-serif' }}>
      {children}
    </button>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────
export const Modal = ({ onClose, title, subtitle, children, wide }) => (
  <div className="fixed inset-0 flex items-center justify-center z-50 px-4"
    style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}>
    <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto`}
      style={{ background: T.white, boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
      <div className="px-8 py-5 flex items-start justify-between"
        style={{ background: `linear-gradient(135deg, ${T.navyDark}, ${T.navy})`, borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div>
          {subtitle && <p className="vrv-tag mb-1">{subtitle}</p>}
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{title}</h2>
        </div>
        <button onClick={onClose} className="text-white/50 hover:text-white text-2xl leading-none mt-0.5 transition-colors">&times;</button>
      </div>
      <div className="px-8 py-6">{children}</div>
    </div>
  </div>
);

// ── Toast ─────────────────────────────────────────────────────────────────────
export const Toast = ({ toast, onClose }) => {
  if (!toast?.show) return null;
  const isSuccess = toast.type === 'success';
  return (
    <div className="vrv-toast" style={{ background: isSuccess ? T.navy : T.danger, color: 'white', border: `1px solid ${isSuccess ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.2)'}` }}>
      <span className="text-lg">{isSuccess ? '✓' : '✕'}</span>
      <span>{toast.message}</span>
      {onClose && <button onClick={onClose} className="opacity-60 hover:opacity-100 ml-2 text-lg leading-none">&times;</button>}
    </div>
  );
};

// ── Search Input ──────────────────────────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: T.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <input type="text" placeholder={placeholder || 'Search...'} value={value} onChange={onChange}
      className="vrv-input pl-10" style={{ minHeight: '44px' }} />
  </div>
);

// ── Reject Modal ──────────────────────────────────────────────────────────────
export const RejectModal = ({ value, onChange, onConfirm, onCancel, title }) => (
  <Modal onClose={onCancel} title={title || 'Reject Content'} subtitle="Reject">
    <textarea value={value} onChange={onChange} placeholder="Reason for rejection (optional)..." rows="3"
      className="vrv-input resize-none mb-4" />
    <div className="flex gap-3 justify-end">
      <ActionBtn onClick={onCancel} color="muted">Cancel</ActionBtn>
      <ActionBtn onClick={onConfirm} color="red">Confirm Reject</ActionBtn>
    </div>
  </Modal>
);

// ── User Detail Fields ────────────────────────────────────────────────────────
export const UserDetailFields = ({ user }) => (
  <div className="grid grid-cols-2 gap-4">
    {[['Name', `${user.name || ''} ${user.surname || ''}`.trim()], ['Email', user.emailID], ['Phone', user.phoneNumber], ['Gothram', user.gothram], ['Role', user.role], ['Status', user.status]].map(([label, val]) => (
      <div key={label}>
        <label className="vrv-label">{label}</label>
        <p className="px-3 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#F8F5EE', color: T.ink, border: '1px solid rgba(0,0,0,0.07)' }}>{val || '—'}</p>
      </div>
    ))}
    <div className="col-span-2">
      <label className="vrv-label">Address</label>
      <p className="px-3 py-2.5 rounded-xl text-sm whitespace-pre-wrap" style={{ background: '#F8F5EE', color: T.ink, border: '1px solid rgba(0,0,0,0.07)' }}>{user.address || '—'}</p>
    </div>
  </div>
);

// ── Table wrappers ────────────────────────────────────────────────────────────
export const TableCard = ({ children }) => (
  <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
    {children}
  </div>
);

export const TableBar = ({ children, className }) => (
  <div className={`flex items-center justify-between px-5 py-3 ${className || ''}`} style={{ background: '#F1EDE4', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
    {children}
  </div>
);

// ── Form Card ─────────────────────────────────────────────────────────────────
export const FormCard = ({ title, subtitle, children }) => (
  <div className="vrv-card mb-6">
    <div className="px-8 py-5" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', background: '#FDFBF7' }}>
      {subtitle && <p className="vrv-tag mb-1">{subtitle}</p>}
      <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: T.ink }}>{title}</h3>
    </div>
    <div className="px-8 py-6">{children}</div>
  </div>
);

// ── Primary Button ────────────────────────────────────────────────────────────
export const PrimaryBtn = ({ onClick, disabled, children, type }) => (
  <button type={type || 'button'} onClick={onClick} disabled={disabled} className="btn-navy">
    {children}
  </button>
);

// ── Stat Card ─────────────────────────────────────────────────────────────────
export const StatCard = ({ icon, label, value, color = T.gold, iconBg }) => (
  <div className="stat-card flex items-center gap-4">
    <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-xl"
      style={{ background: iconBg || `rgba(212,175,55,0.12)` }}>
      {icon}
    </div>
    <div>
      <p className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{value}</p>
      <p className="text-xs uppercase tracking-wider font-medium" style={{ color: T.muted }}>{label}</p>
    </div>
  </div>
);

// ── Page Hero ─────────────────────────────────────────────────────────────────
export const PageHero = ({ tag, title, description, children }) => (
  <section className="vrv-hero">
    <div className="vrv-container">
      <div className="relative z-10 max-w-4xl">
        {tag && <p className="vrv-tag mb-3">{tag}</p>}
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>{title}</h1>
        {description && <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>{description}</p>}
        {children}
      </div>
    </div>
  </section>
);

// ── Section Heading ────────────────────────────────────────────────────────────
export const SectionHeading = ({ tag, title, center }) => (
  <div className={`mb-10 ${center ? 'text-center' : ''}`}>
    {tag && <p className="vrv-tag mb-2">{tag}</p>}
    <h2 className="vrv-section-title">{title}</h2>
    <div className={`vrv-divider mt-4 ${center ? 'mx-auto' : ''}`} style={{ width: '80px' }} />
  </div>
);
